'use client';

import { withMpSso } from '@/lib/payment';
import { useEffect, useState } from 'react';
import { Check, ChevronRight, Clock, ShoppingCart } from 'lucide-react';
import type { InvoiceResponse, InvoiceView } from '@/lib/types';
import { errorText, fetchInvoice, payInvoice } from '@/lib/api';
import { fmtMoney, fmtRange } from '@/lib/format';

const CONFIRM_POLL_MS = 4000;
const CONFIRM_FOR_MS = 3 * 60 * 1000;
import { PaymentPanel, usePaymentWindow } from './PaymentWindow';
import { PayDock } from './PayDock';
import { Avatar } from './Avatar';

/**
 * One card that says where an invoice stands, in words, with the one thing
 * to do next. From MP's rows and nothing else: the browser's return from OLG
 * is a hint, the API's answer is the truth.
 *
 * Mounts in three places and knows nothing about which: inside the event
 * page (`embedded`), the profile widget's list, and a standalone pay page.
 * The back link comes from the invoice's own event, when it has exactly one.
 */
interface Props {
  apiBaseUrl: string;
  guid: string;
  /** OLG's return page said the payment went through. Only softens "unpaid" into "confirming". */
  claimedSuccess?: boolean;
  /** The current page, bare. Where OLG returns to, and the base of the share link. */
  pageUrl: string;
  /** The details page for a back link, `…/events/details/?id=`. */
  detailsUrl: string;
  /** True when rendered inside the event's own page (no back link, no event title). */
  embedded?: boolean;
  /** Told once the invoice is read, so an embedding page can learn its event. */
  onLoaded?: (res: InvoiceResponse) => void;
  compact?: boolean;
  /**
   * Render without the card around it, for a host that is already a card.
   * The money and the people are one thing to a person looking at this page,
   * so on the event page they are one card and this is a band inside it.
   */
  bare?: boolean;
  /**
   * When this invoice still owes money, take it out of the page and dock it
   * to the corner. Everything else — paid, confirming, cancelled, expired —
   * stays where it is: it is news, not a thing to do.
   */
  dockWhenOwed?: boolean;
  /** A face for a contact on the invoice, when the page already knows one. */
  photoOf?: (contactId: number | null) => string | null;
  /**
   * Show the invoice line by line rather than as a list of names. True in the
   * dock, and wherever the invoice is the thing being looked at rather than a
   * status on a page that says the same names again.
   */
  detail?: boolean;
  /**
   * Another card above this one already said this. A household can hold two
   * settled invoices — the free seats and the paid ones are written
   * separately on purpose — and two cards stacked saying "You are in." reads
   * as a rendering bug rather than two records.
   */
  quiet?: boolean;
}

export function InvoiceCard({
  apiBaseUrl,
  guid,
  claimedSuccess = false,
  pageUrl,
  detailsUrl,
  embedded = false,
  onLoaded,
  compact = false,
  bare = false,
  dockWhenOwed = false,
  photoOf,
  detail = false,
  quiet = false,
}: Props) {
  const [res, setRes] = useState<InvoiceResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [paying, setPaying] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  // OLG's page said it went through (from the popup, this visit): softens "unpaid" into "confirming".
  const [claimed, setClaimed] = useState(false);
  const [lastAmount, setLastAmount] = useState<number | undefined>(undefined);
  const pay = usePaymentWindow({
    apiBaseUrl,
    onSettled: (_guid, claimedSuccess) => {
      setPaying(false);
      if (claimedSuccess) setClaimed(true);
      setRefreshKey((k) => k + 1);
    },
  });

  useEffect(() => {
    const controller = new AbortController();
    fetchInvoice(apiBaseUrl, guid, claimedSuccess || claimed, controller.signal)
      .then((r) => {
        setRes(r);
        onLoaded?.(r);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        console.error('[event-details] invoice failed:', err);
        setError('We could not load that registration right now.');
      });
    return () => controller.abort();
    // onLoaded is a notification, not an input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBaseUrl, guid, claimedSuccess, claimed, refreshKey]);

  // "Confirming": OLG said yes but MP's Payments row has not landed yet
  // (OLG posts it to MP a beat after the redirect). Keep reading MP every
  // few seconds for a while, so nobody has to refresh to see "You are in".
  const confirming = res?.view.kind === 'confirming';
  const [confirmingSince, setConfirmingSince] = useState<number | null>(null);
  useEffect(() => {
    if (!confirming) {
      setConfirmingSince(null);
      return;
    }
    setConfirmingSince((t) => t ?? Date.now());
    const started = confirmingSince ?? Date.now();
    if (Date.now() - started > CONFIRM_FOR_MS) return;
    const t = window.setTimeout(() => setRefreshKey((k) => k + 1), CONFIRM_POLL_MS);
    return () => window.clearTimeout(t);
    // refreshKey re-arms the timer after each read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirming, refreshKey]);
  const stillChecking =
    confirming && confirmingSince !== null && Date.now() - confirmingSince <= CONFIRM_FOR_MS;

  const startPay = (amount?: number) => {
    setPaying(true);
    setLastAmount(amount);
    // The window opens on the click; the URL follows once the token is minted.
    const win = pay.begin();
    payInvoice(apiBaseUrl, guid, pageUrl, amount)
      // Paying an invoice from the card is the same trip to OLG, so it
      // carries the same session. See withMpSso.
      .then(({ url }) => (win ? pay.launch(url, guid) : window.location.assign(withMpSso(url))))
      .catch((err) => {
        pay.abort();
        setPaying(false);
        setError(errorText(err, 'Could not start the payment.'));
      });
  };
  const payHere = (amount?: number) => {
    pay.cancel();
    payInvoice(apiBaseUrl, guid, pageUrl, amount)
      .then(({ url }) => window.location.assign(withMpSso(url)))
      .catch((err) => setError(errorText(err, 'Could not start the payment.')));
  };

  const shareUrl = (() => {
    const u = new URL(pageUrl);
    if (res?.event) u.searchParams.set('id', String(res.event.id));
    u.searchParams.set('invoice', guid);
    return u.toString();
  })();

  if (error && !res) {
    return (
      <div
        className={
          bare ? 'text-rose text-sm font-semibold' : 'card text-rose p-5 text-sm font-semibold'
        }
      >
        {error}
      </div>
    );
  }
  if (!res) {
    return (
      <div className={bare ? 'space-y-3' : 'card space-y-3 p-5'}>
        <div className="skeleton h-3 w-24" />
        <div className="skeleton h-6 w-2/3" />
        <div className="skeleton h-4 w-full" />
      </div>
    );
  }

  const { view, event } = res;
  // Owed money floats; everything else stays in the page's own card.
  const owed =
    dockWhenOwed && (view.kind === 'unpaid' || view.kind === 'partly-paid')
      ? view.kind === 'partly-paid'
        ? view.balance
        : view.state.total - view.state.paid
      : 0;
  const docked = owed > 0;
  const pad = docked ? 'p-4' : bare ? '' : compact ? 'p-4' : 'p-6';
  const card = docked ? 'card shadow-lift ' : bare ? '' : 'card ';
  const heading = compact ? 'text-lg' : 'text-2xl';
  const dock = (body: React.ReactNode) =>
    docked ? (
      <PayDock amount={owed} id={guid}>
        {body}
      </PayDock>
    ) : (
      body
    );
  // Docked, the surface around it is the dock's — one element morphing, so
  // this must not draw a second card inside it. Anywhere else it wears its
  // own chrome, bare or not: the band needs an edge to sit flush with.
  const owedCard = docked ? '' : 'card overflow-hidden';
  const owedPad = compact ? 'p-4' : 'p-6';

  if (view.kind === 'missing') {
    return (
      <div className={`${card}${pad} text-center`}>
        <h3 className={`text-navy ${heading} font-extrabold`}>
          We could not find that registration.
        </h3>
        <p className="text-slate mt-1 text-sm">
          The link may be incomplete. Check the email we sent, or find the event and register again.
        </p>
      </div>
    );
  }

  const { state } = view;
  // MP files people as "Wirgau, Weston"; a sentence wants them the other way.
  const spoken = (name: string) => {
    const parts = name.split(',');
    const [last, first] = parts;
    // Exactly two pieces, or it is not the filed form and is left alone.
    return parts.length === 2 && last && first ? `${first.trim()} ${last.trim()}` : name.trim();
  };
  const who = state.participants
    .map((p) => spoken(p.name))
    .filter(Boolean)
    .join(', ');
  const head = !embedded && (
    <div className="mb-3">
      <p className="kicker">{event?.title ?? state.eventTitle ?? 'Registration'}</p>
      {(event?.start ?? state.eventStart) && (
        <p className="text-slate text-sm">{fmtRange(event?.start ?? state.eventStart, null)}</p>
      )}
    </div>
  );
  const names = who && <p className="text-slate-deep text-sm">{who}</p>;

  /**
   * Who is on this invoice, one row each: their face, what they are coming as
   * and their share of the money. Shown where the money is still owed —
   * somebody about to pay $30 for three people is owed the three lines that
   * add up to it.
   *
   * What is NOT here is their answers. This card is reachable by anyone
   * holding the link, which is the point of a pay link, and "anything we
   * should know?" is a medical answer.
   */
  const rosterList = (rule: boolean) => (
    // The rule separates the rows from a heading above them. On the settled
    // card that heading is now one small line, and a full border under it made
    // more of a divider than there was anything to divide.
    <ul className={rule ? 'border-mist mt-2.5 space-y-2 border-t pt-2.5' : 'space-y-2'}>
      {state.participants.map((p) => (
        <li key={p.eventParticipantId} className="flex items-center gap-2.5">
          <Avatar name={spoken(p.name)} image={photoOf?.(p.contactId) ?? null} size={28} />
          <span className="min-w-0 flex-1">
            <span className="text-navy block truncate text-sm font-semibold">{spoken(p.name)}</span>
            {p.items.length > 0 && (
              <span className="text-slate block truncate text-[0.7rem]">{p.items.join(' · ')}</span>
            )}
          </span>
          {/* Nothing where there is nothing to pay. A line reading "Free" on
              an invoice is money language for something that never had a
              price on it. */}
          {p.amount > 0 && (
            <span className="text-navy shrink-0 text-sm font-bold">{fmtMoney(p.amount)}</span>
          )}
        </li>
      ))}
    </ul>
  );
  const backLink = !embedded && event && (
    <a href={`${detailsUrl}${event.id}`} className="btn btn-quiet mt-4">
      Event details <ChevronRight className="h-4 w-4" />
    </a>
  );
  // Who it covers: names on a status card, line by line where the invoice is
  // the subject.
  // The rule above it separates it from a heading; under the cart band there
  // is nothing to separate it from.
  const people = detail || docked ? rosterList(!docked) : names;
  const problem = error && <p className="text-rose mt-2 text-xs font-semibold">{error}</p>;

  /**
   * The header for a card about money owed. No ticket and no "Your spot is
   * held": the band says what this is at a glance, and it is the same cart
   * the card collapses into. The amount is on the button and the people are
   * the rows between, so the heading was the only line here saying nothing
   * the card did not already say.
   */
  // Docked, the band belongs to the dock: it is the thing the card morphed
  // out of, and the way back into it. Anywhere else this card draws its own.
  const cartBand = docked ? null : (
    <div className="bg-teal flex items-center gap-2 px-4 py-3 text-white">
      <ShoppingCart className="h-4 w-4 shrink-0" />
      <span className="text-sm font-bold">Your cart</span>
    </div>
  );

  const Icon = ({ tone, children }: { tone: 'teal' | 'amber'; children: React.ReactNode }) => (
    <span
      className={`${tone === 'teal' ? 'bg-teal-soft text-teal-deep' : 'bg-amber-soft text-amber'} flex h-11 w-11 items-center justify-center rounded-full`}
    >
      {children}
    </span>
  );

  if (view.kind === 'paid') {
    return (
      <div className={`${card}${pad} rise`}>
        {head}
        {/*
         * A SETTLED SEAT IS A LINE, NOT A BILLBOARD.
         *
         * This was a 44px tick, a 24px headline and a rule, stacked, above
         * the one thing anybody came back to check: who is registered and as
         * what. Three quarters of the card was spent congratulating somebody
         * for something they already knew, every time they opened the page —
         * and the celebrating is a moment, not a permanent state. If this
         * night wants a "you're in", it belongs in the one-time flourish the
         * conference and retreat microsites do on arrival, generalised, and
         * not in a card people re-read for the facts.
         *
         * So: a tick and the words on one row, then the people directly
         * under them.
         */}
        {!quiet && (
          <p className="text-navy flex items-center gap-2 text-sm font-bold">
            <span className="bg-teal-soft text-teal-deep flex h-5 w-5 items-center justify-center rounded-full">
              <Check className="h-3 w-3" strokeWidth={3} />
            </span>
            You are in
          </p>
        )}
        {/* No sentence about the money or the email: the rows carry the
            amounts, and a tick with nothing to pay says the rest. */}
        {people}
        {backLink}
      </div>
    );
  }
  if (view.kind === 'partly-paid') {
    return dock(
      <div className={`${owedCard} rise`}>
        {cartBand}
        <div className={owedPad}>
          {head}
          {people}
          <p className="text-slate mt-2 text-sm">
            {fmtMoney(state.paid)} paid of {fmtMoney(state.total)}. {fmtMoney(view.balance)} is
            still due.
          </p>
          {pay.state.kind === 'idle' ? (
            <button
              type="button"
              className="btn btn-primary mt-4"
              onClick={() => startPay()}
              disabled={paying}
            >
              {paying ? 'One moment…' : `Pay the rest, ${fmtMoney(view.balance)}`}
            </button>
          ) : (
            <div className="mt-4">
              <PaymentPanel
                pay={pay}
                amount={view.balance}
                compact
                onPayHere={() => payHere(lastAmount)}
                onOpenAgain={() => startPay(lastAmount)}
                payLaterHref={shareUrl}
              />
            </div>
          )}
          {problem}
        </div>
      </div>
    );
  }
  if (view.kind === 'confirming') {
    return (
      <div className={`${card}${pad} rise`}>
        {head}
        <Icon tone="amber">
          <Clock className="h-5 w-5" />
        </Icon>
        <h3 className={`text-navy mt-3 ${heading} font-extrabold`}>Confirming your payment.</h3>
        {people}
        <p className="text-slate mt-2 text-sm">
          {stillChecking
            ? 'The payment page said it went through. Our records usually catch up within a minute; this card updates on its own.'
            : 'The payment page said it went through. Our records usually catch up within a minute and sometimes take longer. You will get an email either way, and nothing here needs paying twice.'}
        </p>
        {stillChecking ? (
          <p className="text-slate mt-4 inline-flex items-center gap-2 text-xs font-semibold">
            <span
              className="border-teal inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-t-transparent"
              aria-hidden
            />
            Checking with our records…
          </p>
        ) : (
          <button
            type="button"
            className="btn btn-quiet mt-4"
            onClick={() => {
              setConfirmingSince(Date.now());
              setRefreshKey((k) => k + 1);
            }}
          >
            Check again
          </button>
        )}
      </div>
    );
  }
  if (view.kind === 'cancelled') {
    return (
      <div className={`${card}${pad} rise`}>
        {head}
        <h3 className={`text-navy ${heading} font-extrabold`}>This registration was cancelled.</h3>
        {people}
        <p className="text-slate mt-2 text-sm">
          {state.paid > 0
            ? `${fmtMoney(state.paid)} was paid on it. If a refund is due, the office will take care of it and you will hear from them.`
            : 'Nothing was charged.'}{' '}
          To come after all, register again.
        </p>
        {backLink}
      </div>
    );
  }
  if (view.kind === 'expired') {
    return (
      <div className={`${card}${pad} rise`}>
        {head}
        <h3 className={`text-navy ${heading} font-extrabold`}>This registration expired.</h3>
        {people}
        <p className="text-slate mt-2 text-sm">
          Seats are held for a few hours while you pay, and this one ran out. Nothing was charged.
          Register again to hold new seats.
        </p>
        {backLink}
      </div>
    );
  }
  return dock(
    <div className={`${owedCard} rise`}>
      {cartBand}
      <div className={owedPad}>
        {head}
        {people}
        {/* Nothing about the hold expiring. Everybody knows a cart does, and
            the one who needs telling is the person who walked away from it —
            which is an email an hour before it lapses, not a line on a card
            they are already looking at. */}
        {pay.state.kind === 'idle' ? (
          <button
            type="button"
            className="btn btn-primary mt-4"
            onClick={() => startPay()}
            disabled={paying}
          >
            {paying ? 'One moment…' : `Pay ${fmtMoney(state.total - state.paid)}`}
          </button>
        ) : (
          <div className="mt-4">
            <PaymentPanel
              pay={pay}
              amount={state.total - state.paid}
              compact
              onPayHere={() => payHere(lastAmount)}
              onOpenAgain={() => startPay(lastAmount)}
              payLaterHref={shareUrl}
            />
          </div>
        )}
        {problem}
      </div>
    </div>
  );
}

export type { InvoiceView };
